import { useEffect, useMemo, useState } from "react";
import { api, fmtEUR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    ChartBar,
    ArrowsClockwise,
    ArrowUUpLeft,
    Trophy,
    Receipt,
    Calendar,
    Package,
    UsersThree,
    SquaresFour,
    CalendarBlank,
    FilePdf,
    FileXls,
    ChartLineUp,
    Clock,
    CalendarCheck,
    Wallet,
    Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
    LineChart,
    Line,
    Cell,
} from "recharts";
import { exportReportPDF, exportReportExcel } from "@/lib/export-report";

const GR_MONTHS = [
    "ΙΑΝ",
    "ΦΕΒ",
    "ΜΑΡ",
    "ΑΠΡ",
    "ΜΑΪ",
    "ΙΟΥΝ",
    "ΙΟΥΛ",
    "ΑΥΓ",
    "ΣΕΠ",
    "ΟΚΤ",
    "ΝΟΕ",
    "ΔΕΚ",
];
const monthLabel = (k) => {
    if (!k || !/^\d{4}-\d{2}$/.test(k)) return k || "";
    const [y, m] = k.split("-");
    return `${GR_MONTHS[parseInt(m, 10) - 1] || m} ${y}`;
};

const fmtNumber = (v) =>
    new Intl.NumberFormat("el-GR", { maximumFractionDigits: 2 }).format(
        Number(v) || 0
    );

const fmtDate = (s) => {
    if (!s) return "—";
    return s.length >= 10 ? s.substring(0, 10) : s;
};

const fmtDateTime = (s) => {
    if (!s) return "—";
    try {
        return new Date(s).toLocaleString("el-GR", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return s;
    }
};

// Build month options: previous 12 + current
function buildMonthOptions() {
    const opts = [];
    const now = new Date();
    // Always exactly 12 months going back from current month
    for (let i = 0; i < 12; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        opts.push({ key: k, label: monthLabel(k) });
    }
    return opts;
}

function todayStr() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function firstOfMonth() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

// Date utilities for quick-pick presets
function ymd(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function presetRanges() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    // ISO week (Monday-Sunday)
    const weekStart = new Date(today);
    const dow = (weekStart.getDay() + 6) % 7; // 0 = Monday
    weekStart.setDate(weekStart.getDate() - dow);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    // This month
    const mStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const mEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    // Last month
    const lmStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lmEnd = new Date(today.getFullYear(), today.getMonth(), 0);
    return [
        { id: "today", label: "ΣΗΜΕΡΑ", from: ymd(today), to: ymd(today) },
        { id: "yesterday", label: "ΧΘΕΣ", from: ymd(yesterday), to: ymd(yesterday) },
        { id: "week", label: "ΑΥΤΗ Η ΕΒΔΟΜΑΔΑ", from: ymd(weekStart), to: ymd(weekEnd) },
        { id: "month", label: "ΑΥΤΟΣ Ο ΜΗΝΑΣ", from: ymd(mStart), to: ymd(mEnd) },
        { id: "lastmonth", label: "ΠΡΟΗΓ. ΜΗΝΑΣ", from: ymd(lmStart), to: ymd(lmEnd) },
    ];
}

export default function Reports() {
    const [activeTab, setActiveTab] = useState("overview");

    // Period filter — shared across the range-aware tabs
    const [mode, setMode] = useState("month"); // 'month' | 'range'
    const [month, setMonth] = useState(buildMonthOptions()[0].key);
    const [fromDate, setFromDate] = useState(firstOfMonth());
    const [toDate, setToDate] = useState(todayStr());

    const queryParams = useMemo(() => {
        if (mode === "month") return { month };
        return { from_date: fromDate, to_date: toDate };
    }, [mode, month, fromDate, toDate]);

    const periodLabel = useMemo(() => {
        if (mode === "month") return monthLabel(month);
        return `${fmtDate(fromDate)} → ${fmtDate(toDate)}`;
    }, [mode, month, fromDate, toDate]);

    return (
        <div
            className="flex-1 min-h-0 overflow-y-auto p-6 md:p-10 max-w-7xl w-full"
            data-testid="reports-page"
        >
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4 fade-up">
                <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5F66] mb-2">
                        ΑΝΑΦΟΡΕΣ & ΣΤΑΤΙΣΤΙΚΑ
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">
                        REPORTS
                    </h1>
                    <p className="mt-2 text-sm text-[#5C5F66] font-mono">
                        Περίοδος: <strong>{periodLabel}</strong>
                    </p>
                </div>
            </div>

            {/* Period Filter */}
            <div
                className="mb-6 border-2 border-[#0A0A0A] bg-white p-4 fade-up"
                data-testid="period-filter"
            >
                <div className="flex items-center gap-3 flex-wrap">
                    <div
                        className="inline-flex items-stretch border-2 border-[#0A0A0A]"
                        data-testid="period-mode"
                    >
                        <button
                            onClick={() => setMode("month")}
                            data-testid="period-mode-month"
                            className={`px-4 h-11 text-xs font-bold uppercase tracking-[0.15em] border-r-2 border-[#0A0A0A] transition-colors ${
                                mode === "month"
                                    ? "bg-[#0A0A0A] text-white"
                                    : "bg-white text-[#5C5F66] hover:bg-[#F4F5F7]"
                            }`}
                        >
                            ΜΗΝΑΣ
                        </button>
                        <button
                            onClick={() => setMode("range")}
                            data-testid="period-mode-range"
                            className={`px-4 h-11 text-xs font-bold uppercase tracking-[0.15em] transition-colors ${
                                mode === "range"
                                    ? "bg-[#0A0A0A] text-white"
                                    : "bg-white text-[#5C5F66] hover:bg-[#F4F5F7]"
                            }`}
                        >
                            ΕΥΡΟΣ
                        </button>
                    </div>

                    {mode === "month" ? (
                        <Select value={month} onValueChange={setMonth}>
                            <SelectTrigger
                                data-testid="period-month-select"
                                className="rounded-none h-11 w-48 border-2 border-[#0A0A0A] font-mono"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="rounded-none max-h-72">
                                {buildMonthOptions().map((o) => (
                                    <SelectItem key={o.key} value={o.key}>
                                        {o.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <div className="flex items-end gap-3">
                            <div>
                                <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C5F66]">
                                    ΑΠΟ
                                </Label>
                                <Input
                                    type="date"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    data-testid="period-from"
                                    className="rounded-none h-11 w-40 border-2 border-[#0A0A0A] font-mono"
                                />
                            </div>
                            <div>
                                <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C5F66]">
                                    ΕΩΣ
                                </Label>
                                <Input
                                    type="date"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    data-testid="period-to"
                                    className="rounded-none h-11 w-40 border-2 border-[#0A0A0A] font-mono"
                                />
                            </div>
                        </div>
                    )}
                </div>
                {/* Quick-pick presets — visible only in range mode */}
                {mode === "range" && (
                    <div
                        className="mt-3 pt-3 border-t border-[#E2E4E9] flex items-center gap-2 flex-wrap"
                        data-testid="period-presets"
                    >
                        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C5F66]">
                            ΓΡΗΓΟΡΗ ΕΠΙΛΟΓΗ:
                        </span>
                        {presetRanges().map((p) => {
                            const active =
                                fromDate === p.from && toDate === p.to;
                            return (
                                <button
                                    key={p.id}
                                    onClick={() => {
                                        setFromDate(p.from);
                                        setToDate(p.to);
                                    }}
                                    data-testid={`period-preset-${p.id}`}
                                    className={`px-3 h-9 text-[11px] font-black uppercase tracking-wider border-2 transition-colors ${
                                        active
                                            ? "bg-[#002FA7] text-white border-[#002FA7]"
                                            : "bg-white text-[#0A0A0A] border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
                                    }`}
                                >
                                    {p.label}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="rounded-none bg-white border-2 border-[#0A0A0A] p-0 h-auto flex-wrap">
                    <TabTrigger value="overview" icon={ChartBar} label="ΕΠΙΣΚΟΠΗΣΗ" />
                    <TabTrigger value="items" icon={Package} label="ΕΙΔΗ" />
                    <TabTrigger value="customers" icon={UsersThree} label="ΠΕΛΑΤΕΣ" />
                    <TabTrigger value="categories" icon={SquaresFour} label="ΚΑΤΗΓΟΡΙΕΣ" />
                    <TabTrigger value="daily" icon={CalendarBlank} label="ΗΜΕΡΗΣΙΑ" />
                    <TabTrigger value="hours" icon={Clock} label="ΩΡΕΣ" />
                    <TabTrigger value="weekdays" icon={CalendarCheck} label="ΗΜΕΡΕΣ ΕΒΔΟΜΑΔΑΣ" />
                    <TabTrigger value="compare" icon={ChartLineUp} label="ΣΥΓΚΡΙΣΗ" />
                    <TabTrigger value="returns" icon={ArrowUUpLeft} label="ΕΠΙΣΤΡΟΦΕΣ" />
                </TabsList>

                <TabsContent value="overview" className="mt-6">
                    <OverviewSection />
                </TabsContent>
                <TabsContent value="items" className="mt-6">
                    <ItemsSoldSection
                        params={queryParams}
                        periodLabel={periodLabel}
                    />
                </TabsContent>
                <TabsContent value="customers" className="mt-6">
                    <CustomersSpendSection
                        params={queryParams}
                        periodLabel={periodLabel}
                    />
                </TabsContent>
                <TabsContent value="categories" className="mt-6">
                    <CategoriesSection
                        params={queryParams}
                        periodLabel={periodLabel}
                    />
                </TabsContent>
                <TabsContent value="daily" className="mt-6">
                    <DailySection
                        params={queryParams}
                        periodLabel={periodLabel}
                    />
                </TabsContent>
                <TabsContent value="hours" className="mt-6">
                    <HoursSection
                        params={queryParams}
                        periodLabel={periodLabel}
                    />
                </TabsContent>
                <TabsContent value="weekdays" className="mt-6">
                    <WeekdaysSection
                        params={queryParams}
                        periodLabel={periodLabel}
                    />
                </TabsContent>
                <TabsContent value="compare" className="mt-6">
                    <CompareMonthsSection />
                </TabsContent>
                <TabsContent value="returns" className="mt-6">
                    <ReturnsSection
                        params={queryParams}
                        periodLabel={periodLabel}
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
}

function TabTrigger({ value, icon: Icon, label }) {
    return (
        <TabsTrigger
            value={value}
            data-testid={`tab-${value}`}
            className="rounded-none data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white px-4 py-3 text-xs font-bold uppercase tracking-[0.15em] flex items-center gap-2"
        >
            <Icon size={14} weight="bold" />
            {label}
        </TabsTrigger>
    );
}

// ===== Overview (existing summary, kept) =====
function OverviewSection() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get("/reports/summary");
            setData(res.data);
        } catch {
            toast.error("Αποτυχία φόρτωσης");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    if (loading)
        return <div className="p-12 text-center text-sm text-[#5C5F66]">Φόρτωση...</div>;
    if (!data)
        return (
            <div className="p-12 text-center text-sm text-[#5C5F66]">
                Σφάλμα φόρτωσης δεδομένων.
            </div>
        );

    return (
        <div className="space-y-6">
            <div className="flex justify-end">
                <Button
                    onClick={load}
                    variant="outline"
                    data-testid="reports-refresh"
                    className="rounded-none h-10 border-[#0A0A0A] font-bold"
                >
                    <ArrowsClockwise size={14} weight="bold" className="mr-2" />
                    ΑΝΑΝΕΩΣΗ
                </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Kpi
                    label="ΣΗΜΕΡΑ"
                    value={fmtEUR(data.today_total_gross)}
                    sub={`${data.today_sales_count} πωλήσεις`}
                    icon={Calendar}
                    color="#002FA7"
                />
                <Kpi
                    label="ΜΗΝΑ"
                    value={fmtEUR(data.month_total_gross)}
                    sub={`${data.month_sales_count} πωλήσεις`}
                    icon={ChartBar}
                    color="#34C759"
                />
                <Kpi
                    label="ΦΠΑ ΜΗΝΑ"
                    value={fmtEUR(data.month_total_vat)}
                    sub={`Καθ. ${fmtEUR(data.month_total_net)}`}
                    icon={Receipt}
                    color="#FFCC00"
                    dark
                />
                <Kpi
                    label="ΧΡΕΩΣΕΙΣ ΜΗΝΑ"
                    value={fmtEUR(data.month_amount_charged)}
                    sub={`${data.total_customers} πελάτες · ${data.total_items} είδη`}
                    icon={Trophy}
                    color="#0A0A0A"
                />
            </div>

            {/* Regular customers credit pool */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Kpi
                    label="ΑΠΟΜΕΝΟΥΝ ΣΕ REGULAR"
                    value={fmtEUR(data.regulars_remaining_total || 0)}
                    sub={
                        data.period_limit_set
                            ? `Από σύνολο ${fmtEUR(data.regulars_pool_total)} (${data.regulars_count} πελάτες × ${fmtEUR(data.period_limit)})`
                            : "Δεν έχει οριστεί όριο μήνα"
                    }
                    icon={Wallet}
                    color="#002FA7"
                    testid="kpi-regulars-remaining"
                />
                <Kpi
                    label="ΧΡΗΣΙΜΟΠ. ΑΠΟ REGULAR"
                    value={fmtEUR(data.regulars_used_total || 0)}
                    sub={
                        data.regulars_pool_total > 0
                            ? `${Math.round((100 * (data.regulars_used_total || 0)) / data.regulars_pool_total)}% του pool`
                            : "—"
                    }
                    icon={ChartBar}
                    color="#34C759"
                    testid="kpi-regulars-used"
                />
                <Kpi
                    label="ΣΥΜΠΛΗΡΩΣΑΝ ΟΡΙΟ"
                    value={String(data.customers_at_limit_count || 0)}
                    sub={
                        data.customers_at_limit_count > 0
                            ? "πελάτες έπιασαν το όριο"
                            : "Κανείς δεν έπιασε το όριο"
                    }
                    icon={Warning}
                    color="#FF3B30"
                    testid="kpi-customers-at-limit"
                />
            </div>

            {/* Customers who hit the limit — list */}
            {data.customers_at_limit && data.customers_at_limit.length > 0 && (
                <div
                    className="border-2 border-[#FF3B30] bg-white"
                    data-testid="customers-at-limit-list"
                >
                    <div className="bg-[#FF3B30] text-white px-4 py-2 flex items-center gap-2">
                        <Warning size={14} weight="bold" />
                        <span className="text-[11px] font-bold uppercase tracking-[0.2em]">
                            ΠΕΛΑΤΕΣ ΠΟΥ ΕΦΤΑΣΑΝ ΤΟ ΟΡΙΟ ΤΟΥΣ ({data.customers_at_limit_count})
                        </span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-[#F4F5F7]">
                                <tr className="border-b border-[#E2E4E9]">
                                    <th className="text-left p-3 text-[10px] font-bold uppercase tracking-[0.15em] text-[#5C5F66]">
                                        ΚΩΔΙΚΟΣ
                                    </th>
                                    <th className="text-left p-3 text-[10px] font-bold uppercase tracking-[0.15em] text-[#5C5F66]">
                                        ΠΕΛΑΤΗΣ
                                    </th>
                                    <th className="text-right p-3 text-[10px] font-bold uppercase tracking-[0.15em] text-[#5C5F66]">
                                        ΧΡΕΩΘΗΚΕ
                                    </th>
                                    <th className="text-left p-3 text-[10px] font-bold uppercase tracking-[0.15em] text-[#5C5F66]">
                                        ΠΟΤΕ ΕΦΤΑΣΕ
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.customers_at_limit.map((c) => (
                                    <tr
                                        key={c.customer_id}
                                        className="border-b border-[#E2E4E9] hover:bg-[#F4F5F7] transition-colors"
                                        data-testid={`limit-row-${c.customer_code}`}
                                    >
                                        <td className="p-3 font-mono">{c.customer_code}</td>
                                        <td className="p-3 font-medium">{c.customer_name}</td>
                                        <td className="p-3 text-right font-mono tabular-nums">
                                            {fmtEUR(c.used)}
                                        </td>
                                        <td className="p-3 font-mono text-[#5C5F66]">
                                            {fmtDateTime(c.limit_reached_at)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

function Kpi({ label, value, sub, icon: Icon, color, dark, testid }) {
    return (
        <div
            className="border-2 p-4"
            style={{
                borderColor: color,
                backgroundColor: dark ? color : "white",
                color: "#0A0A0A",
            }}
            data-testid={testid}
        >
            <div className="flex items-center gap-2 mb-2">
                <Icon size={16} weight="bold" style={{ color: dark ? "#0A0A0A" : color }} />
                <div className="text-[10px] font-bold uppercase tracking-[0.25em]">{label}</div>
            </div>
            <div className="font-heading text-2xl md:text-3xl font-black tracking-tighter">
                {value}
            </div>
            <div className="text-[10px] text-[#5C5F66] mt-1 font-mono">{sub}</div>
        </div>
    );
}

// ===== Generic data-loading hook =====
function useReport(endpoint, params) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        api.get(endpoint, { params })
            .then((r) => alive && setData(r.data))
            .catch(() => alive && toast.error("Αποτυχία φόρτωσης"))
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endpoint, JSON.stringify(params)]);

    return { data, loading };
}

// ===== Common report shell =====
function ReportShell({ title, periodLabel, loading, data, columns, totals, fileName, children }) {
    const exportPDF = async () => {
        if (!data || data.rows.length === 0) {
            toast.error("Καμία εγγραφή προς εξαγωγή");
            return;
        }
        try {
            await exportReportPDF({
                title,
                subtitle: periodLabel,
                columns,
                rows: data.rows,
                totals,
                fileName,
                orientation: "landscape",
            });
        } catch {
            toast.error("Σφάλμα εξαγωγής PDF");
        }
    };
    const exportXLS = () => {
        if (!data || data.rows.length === 0) {
            toast.error("Καμία εγγραφή προς εξαγωγή");
            return;
        }
        try {
            exportReportExcel({
                title,
                subtitle: periodLabel,
                columns,
                rows: data.rows,
                totals,
                fileName,
            });
        } catch {
            toast.error("Σφάλμα εξαγωγής Excel");
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <h2 className="font-heading text-2xl font-black tracking-tighter">
                    {title}
                </h2>
                <div className="flex items-center gap-2">
                    <Button
                        onClick={exportPDF}
                        data-testid="report-export-pdf"
                        className="rounded-none h-10 bg-white border-2 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white font-bold text-xs uppercase tracking-wider"
                    >
                        <FilePdf size={14} weight="bold" className="mr-2" />
                        PDF
                    </Button>
                    <Button
                        onClick={exportXLS}
                        data-testid="report-export-excel"
                        className="rounded-none h-10 bg-white border-2 border-[#1F8A3E] text-[#1F8A3E] hover:bg-[#1F8A3E] hover:text-white font-bold text-xs uppercase tracking-wider"
                    >
                        <FileXls size={14} weight="bold" className="mr-2" />
                        EXCEL
                    </Button>
                </div>
            </div>
            {loading ? (
                <div className="p-12 text-center text-sm text-[#5C5F66]">Φόρτωση...</div>
            ) : !data || data.rows.length === 0 ? (
                <div className="p-12 text-center text-sm text-[#5C5F66] border border-[#E2E4E9] bg-white">
                    Δεν υπάρχουν εγγραφές για αυτή την περίοδο.
                </div>
            ) : (
                children
            )}
        </div>
    );
}

// ===== ITEMS SOLD =====
function ItemsSoldSection({ params, periodLabel }) {
    const { data, loading } = useReport("/reports/items-sold", params);

    const columns = [
        { key: "item_code", label: "ΚΩΔΙΚΟΣ" },
        { key: "description", label: "ΠΕΡΙΓΡΑΦΗ" },
        {
            key: "vat_rate",
            label: "ΦΠΑ%",
            align: "right",
            format: (v) => `${v}%`,
        },
        {
            key: "quantity",
            label: "ΠΟΣΟΤΗΤΑ",
            align: "right",
            numeric: true,
            format: fmtNumber,
        },
        {
            key: "sales_count",
            label: "ΠΩΛΗΣΕΙΣ",
            align: "right",
            numeric: true,
            format: (v) => String(v),
        },
        {
            key: "net",
            label: "ΚΑΘΑΡΑ",
            align: "right",
            numeric: true,
            format: fmtEUR,
        },
        {
            key: "vat",
            label: "ΦΠΑ €",
            align: "right",
            numeric: true,
            format: fmtEUR,
        },
        {
            key: "gross",
            label: "ΣΥΝΟΛΟ",
            align: "right",
            numeric: true,
            format: fmtEUR,
        },
    ];
    const totals = data
        ? [
              { key: "quantity", value: data.totals.quantity, format: fmtNumber },
              { key: "net", value: data.totals.net, format: fmtEUR },
              { key: "vat", value: data.totals.vat, format: fmtEUR },
              { key: "gross", value: data.totals.gross, format: fmtEUR },
          ]
        : [];

    return (
        <ReportShell
            title="ΕΙΔΗ ΠΟΥ ΠΩΛΗΘΗΚΑΝ"
            periodLabel={periodLabel}
            loading={loading}
            data={data}
            columns={columns}
            totals={totals}
            fileName={`einai-${periodLabel.replace(/\s|→/g, "_")}`}
        >
            {/* Top-10 horizontal bar chart */}
            {data && data.rows.length > 0 && (
                <div
                    className="border border-[#E2E4E9] bg-white p-4"
                    data-testid="items-top10-chart"
                >
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5F66] mb-2">
                        TOP 10 ΕΙΔΗ ΑΝΑ ΤΖΙΡΟ
                    </div>
                    <div style={{ width: "100%", height: 320, minHeight: 320 }}>
                        <ResponsiveContainer>
                            <BarChart
                                layout="vertical"
                                data={data.rows.slice(0, 10).map((r) => ({
                                    name:
                                        r.description.length > 22
                                            ? r.description.slice(0, 21) + "…"
                                            : r.description,
                                    "Τζίρος": r.gross,
                                }))}
                                margin={{ top: 5, right: 20, left: 5, bottom: 0 }}
                            >
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke="#E2E4E9"
                                    horizontal={false}
                                />
                                <XAxis
                                    type="number"
                                    tick={{ fontSize: 11, fill: "#5C5F66" }}
                                    tickFormatter={(v) => `${v}€`}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    width={150}
                                    tick={{ fontSize: 11, fill: "#0A0A0A" }}
                                />
                                <Tooltip
                                    formatter={(v) => fmtEUR(v)}
                                    contentStyle={{
                                        border: "2px solid #0A0A0A",
                                        borderRadius: 0,
                                        fontFamily: "monospace",
                                        fontSize: 12,
                                    }}
                                />
                                <Bar dataKey="Τζίρος" fill="#002FA7" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}
            <SimpleTable rows={data?.rows || []} columns={columns} totals={totals} />
            {data && (
                <div className="text-xs text-[#5C5F66] font-mono">
                    {data.totals.items_count} διαφορετικά είδη πωλήθηκαν
                </div>
            )}
        </ReportShell>
    );
}

// ===== CUSTOMERS SPEND =====
function CustomersSpendSection({ params, periodLabel }) {
    const { data, loading } = useReport("/reports/customers-spend", params);

    const columns = [
        { key: "customer_code", label: "ΚΩΔΙΚΟΣ" },
        { key: "customer_name", label: "ΠΕΛΑΤΗΣ" },
        {
            key: "sales_count",
            label: "ΠΩΛΗΣΕΙΣ",
            align: "right",
            numeric: true,
            format: (v) => String(v),
        },
        {
            key: "total_gross",
            label: "ΣΥΝΟΛΟ ΑΓΟΡΩΝ",
            align: "right",
            numeric: true,
            format: fmtEUR,
        },
        {
            key: "amount_charged",
            label: "ΧΡΕΩΘΗΚΕ",
            align: "right",
            numeric: true,
            format: fmtEUR,
        },
        {
            key: "limit_reached_at",
            label: "ΣΥΜΠΛΗΡΩΣΕ ΟΡΙΟ",
            format: (v, row) => {
                if (row?.is_vip) return "VIP";
                if (!v) return "—";
                return fmtDateTime(v);
            },
        },
    ];
    const totals = data
        ? [
              {
                  key: "sales_count",
                  value: data.totals.sales_count,
                  format: (v) => String(v),
              },
              {
                  key: "total_gross",
                  value: data.totals.total_gross,
                  format: fmtEUR,
              },
              {
                  key: "amount_charged",
                  value: data.totals.amount_charged,
                  format: fmtEUR,
              },
          ]
        : [];

    return (
        <ReportShell
            title="ΚΑΤΑΝΑΛΩΣΗ ΠΕΛΑΤΩΝ"
            periodLabel={periodLabel}
            loading={loading}
            data={data}
            columns={columns}
            totals={totals}
            fileName={`pelates-${periodLabel.replace(/\s|→/g, "_")}`}
        >
            <SimpleTable rows={data?.rows || []} columns={columns} totals={totals} />
            {data && (
                <div className="text-xs text-[#5C5F66] font-mono">
                    {data.totals.customers_count} πελάτες πραγματοποίησαν αγορές
                </div>
            )}
        </ReportShell>
    );
}

// ===== CATEGORIES =====
function CategoriesSection({ params, periodLabel }) {
    const { data, loading } = useReport("/reports/categories", params);

    const columns = [
        { key: "category", label: "ΚΑΤΗΓΟΡΙΑ" },
        {
            key: "quantity",
            label: "ΠΟΣΟΤΗΤΑ",
            align: "right",
            numeric: true,
            format: fmtNumber,
        },
        {
            key: "net",
            label: "ΚΑΘΑΡΑ",
            align: "right",
            numeric: true,
            format: fmtEUR,
        },
        {
            key: "vat",
            label: "ΦΠΑ €",
            align: "right",
            numeric: true,
            format: fmtEUR,
        },
        {
            key: "gross",
            label: "ΣΥΝΟΛΟ",
            align: "right",
            numeric: true,
            format: fmtEUR,
        },
    ];
    const totals = data
        ? [
              { key: "quantity", value: data.totals.quantity, format: fmtNumber },
              { key: "net", value: data.totals.net, format: fmtEUR },
              { key: "vat", value: data.totals.vat, format: fmtEUR },
              { key: "gross", value: data.totals.gross, format: fmtEUR },
          ]
        : [];

    return (
        <ReportShell
            title="ΠΩΛΗΣΕΙΣ ΑΝΑ ΚΑΤΗΓΟΡΙΑ"
            periodLabel={periodLabel}
            loading={loading}
            data={data}
            columns={columns}
            totals={totals}
            fileName={`kategories-${periodLabel.replace(/\s|→/g, "_")}`}
        >
            <SimpleTable rows={data?.rows || []} columns={columns} totals={totals} />
        </ReportShell>
    );
}

// ===== DAILY =====
function DailySection({ params, periodLabel }) {
    const { data, loading } = useReport("/reports/daily", params);

    const columns = [
        { key: "date", label: "ΗΜΕΡΟΜΗΝΙΑ" },
        {
            key: "count",
            label: "ΠΩΛΗΣΕΙΣ",
            align: "right",
            numeric: true,
            format: (v) => String(v),
        },
        {
            key: "gross",
            label: "ΑΞΙΑ ΑΓΟΡΩΝ",
            align: "right",
            numeric: true,
            format: fmtEUR,
        },
        {
            key: "amount_charged",
            label: "ΧΡΕΩΘΗΚΕ",
            align: "right",
            numeric: true,
            format: fmtEUR,
        },
    ];
    const totals = data
        ? [
              { key: "count", value: data.totals.count, format: (v) => String(v) },
              { key: "gross", value: data.totals.gross, format: fmtEUR },
              {
                  key: "amount_charged",
                  value: data.totals.amount_charged,
                  format: fmtEUR,
              },
          ]
        : [];

    return (
        <ReportShell
            title="ΗΜΕΡΗΣΙΑ ΑΝΑΛΥΣΗ ΠΩΛΗΣΕΩΝ"
            periodLabel={periodLabel}
            loading={loading}
            data={data}
            columns={columns}
            totals={totals}
            fileName={`imerisia-${periodLabel.replace(/\s|→/g, "_")}`}
        >
            {/* Bar chart (existing) */}
            <div
                className="border border-[#E2E4E9] bg-white p-4"
                data-testid="daily-chart"
            >
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5F66] mb-2">
                    ΕΞΕΛΙΞΗ ΠΩΛΗΣΕΩΝ ΑΝΑ ΗΜΕΡΑ
                </div>
                <div style={{ width: "100%", height: 280, minHeight: 280 }}>
                    <ResponsiveContainer>
                        <BarChart
                            data={(data?.rows || []).map((r) => ({
                                date: r.date.slice(5), // MM-DD
                                "Αξία αγορών": r.gross,
                                "Χρεώθηκε": r.amount_charged,
                            }))}
                            margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#E2E4E9"
                            />
                            <XAxis
                                dataKey="date"
                                tick={{ fontSize: 11, fill: "#5C5F66" }}
                            />
                            <YAxis
                                tick={{ fontSize: 11, fill: "#5C5F66" }}
                                tickFormatter={(v) => `${v}€`}
                            />
                            <Tooltip
                                formatter={(v) => fmtEUR(v)}
                                contentStyle={{
                                    border: "2px solid #0A0A0A",
                                    borderRadius: 0,
                                    fontFamily: "monospace",
                                    fontSize: 12,
                                }}
                            />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Bar dataKey="Αξία αγορών" fill="#002FA7" />
                            <Bar dataKey="Χρεώθηκε" fill="#34C759" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Line trend (new) */}
            {data && data.rows.length > 1 && (
                <div
                    className="border border-[#E2E4E9] bg-white p-4"
                    data-testid="daily-trend-chart"
                >
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5F66] mb-2">
                        ΤΑΣΗ ΗΜΕΡΗΣΙΩΝ ΠΩΛΗΣΕΩΝ
                    </div>
                    <div style={{ width: "100%", height: 220, minHeight: 220 }}>
                        <ResponsiveContainer>
                            <LineChart
                                data={data.rows.map((r) => ({
                                    date: r.date.slice(5),
                                    "Πωλήσεις": r.count,
                                    "Τζίρος": r.gross,
                                }))}
                                margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E4E9" />
                                <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 11, fill: "#5C5F66" }}
                                />
                                <YAxis
                                    yAxisId="left"
                                    tick={{ fontSize: 11, fill: "#5C5F66" }}
                                />
                                <YAxis
                                    yAxisId="right"
                                    orientation="right"
                                    tick={{ fontSize: 11, fill: "#5C5F66" }}
                                    tickFormatter={(v) => `${v}€`}
                                />
                                <Tooltip
                                    contentStyle={{
                                        border: "2px solid #0A0A0A",
                                        borderRadius: 0,
                                        fontFamily: "monospace",
                                        fontSize: 12,
                                    }}
                                />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <Line
                                    yAxisId="left"
                                    type="monotone"
                                    dataKey="Πωλήσεις"
                                    stroke="#FF3B30"
                                    strokeWidth={2}
                                    dot={{ r: 3, fill: "#FF3B30" }}
                                />
                                <Line
                                    yAxisId="right"
                                    type="monotone"
                                    dataKey="Τζίρος"
                                    stroke="#002FA7"
                                    strokeWidth={2}
                                    dot={{ r: 3, fill: "#002FA7" }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}
            <SimpleTable rows={data?.rows || []} columns={columns} totals={totals} />
        </ReportShell>
    );
}

// ===== HOURS (per hour of day) =====
function HoursSection({ params, periodLabel }) {
    const { data, loading } = useReport("/reports/hours", params);

    const columns = [
        { key: "label", label: "ΩΡΑ" },
        {
            key: "count",
            label: "ΠΩΛΗΣΕΙΣ",
            align: "right",
            numeric: true,
            format: (v) => String(v),
        },
        {
            key: "gross",
            label: "ΤΖΙΡΟΣ",
            align: "right",
            numeric: true,
            format: fmtEUR,
        },
        {
            key: "amount_charged",
            label: "ΧΡΕΩΘΗΚΕ",
            align: "right",
            numeric: true,
            format: fmtEUR,
        },
    ];
    const totals = data
        ? [
              { key: "count", value: data.totals.count, format: (v) => String(v) },
              { key: "gross", value: data.totals.gross, format: fmtEUR },
              { key: "amount_charged", value: data.totals.amount_charged, format: fmtEUR },
          ]
        : [];

    const peakLabel =
        data?.peak_hour !== undefined && data?.peak_hour !== null
            ? `${String(data.peak_hour).padStart(2, "0")}:00`
            : null;

    return (
        <ReportShell
            title="ΠΩΛΗΣΕΙΣ ΑΝΑ ΩΡΑ"
            periodLabel={periodLabel}
            loading={loading}
            data={data}
            columns={columns}
            totals={totals}
            fileName={`hours-${periodLabel.replace(/\s|→/g, "_")}`}
        >
            {peakLabel && (
                <div
                    className="inline-flex items-center gap-2 border-2 border-[#002FA7] bg-[#002FA7] text-white px-4 py-2"
                    data-testid="hours-peak"
                >
                    <Clock size={14} weight="bold" />
                    <span className="text-[11px] font-bold uppercase tracking-[0.2em]">
                        ΩΡΑ ΑΙΧΜΗΣ:
                    </span>
                    <span className="font-mono font-black tracking-tight">{peakLabel}</span>
                </div>
            )}
            <div
                className="border border-[#E2E4E9] bg-white p-4"
                data-testid="hours-chart"
            >
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5F66] mb-2">
                    ΠΩΛΗΣΕΙΣ ΑΝΑ ΩΡΑ ΤΗΣ ΗΜΕΡΑΣ
                </div>
                <div style={{ width: "100%", height: 280, minHeight: 280 }}>
                    <ResponsiveContainer>
                        <BarChart
                            data={(data?.rows || []).map((r) => ({
                                hour: r.label,
                                "Πωλήσεις": r.count,
                                "Τζίρος": r.gross,
                            }))}
                            margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E4E9" />
                            <XAxis
                                dataKey="hour"
                                tick={{ fontSize: 10, fill: "#5C5F66" }}
                                interval={0}
                                angle={-45}
                                textAnchor="end"
                                height={50}
                            />
                            <YAxis
                                yAxisId="left"
                                tick={{ fontSize: 11, fill: "#5C5F66" }}
                            />
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                tick={{ fontSize: 11, fill: "#5C5F66" }}
                                tickFormatter={(v) => `${v}€`}
                            />
                            <Tooltip
                                contentStyle={{
                                    border: "2px solid #0A0A0A",
                                    borderRadius: 0,
                                    fontFamily: "monospace",
                                    fontSize: 12,
                                }}
                            />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Bar yAxisId="left" dataKey="Πωλήσεις" fill="#FF3B30">
                                {(data?.rows || []).map((r, i) => (
                                    <Cell
                                        key={`hr-${i}`}
                                        fill={
                                            data?.peak_hour === r.hour
                                                ? "#002FA7"
                                                : "#FF3B30"
                                        }
                                    />
                                ))}
                            </Bar>
                            <Bar yAxisId="right" dataKey="Τζίρος" fill="#0A0A0A" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
            <SimpleTable rows={data?.rows || []} columns={columns} totals={totals} />
        </ReportShell>
    );
}

// ===== WEEKDAYS =====
function WeekdaysSection({ params, periodLabel }) {
    const { data, loading } = useReport("/reports/weekdays", params);

    const columns = [
        { key: "label", label: "ΗΜΕΡΑ" },
        {
            key: "count",
            label: "ΠΩΛΗΣΕΙΣ",
            align: "right",
            numeric: true,
            format: (v) => String(v),
        },
        {
            key: "gross",
            label: "ΤΖΙΡΟΣ",
            align: "right",
            numeric: true,
            format: fmtEUR,
        },
        {
            key: "amount_charged",
            label: "ΧΡΕΩΘΗΚΕ",
            align: "right",
            numeric: true,
            format: fmtEUR,
        },
    ];
    const totals = data
        ? [
              { key: "count", value: data.totals.count, format: (v) => String(v) },
              { key: "gross", value: data.totals.gross, format: fmtEUR },
              { key: "amount_charged", value: data.totals.amount_charged, format: fmtEUR },
          ]
        : [];

    const wdLabels = ["ΔΕΥΤΕΡΑ", "ΤΡΙΤΗ", "ΤΕΤΑΡΤΗ", "ΠΕΜΠΤΗ", "ΠΑΡΑΣΚΕΥΗ", "ΣΑΒΒΑΤΟ", "ΚΥΡΙΑΚΗ"];
    const peakLabel =
        data?.peak_weekday !== undefined && data?.peak_weekday !== null
            ? wdLabels[data.peak_weekday]
            : null;

    return (
        <ReportShell
            title="ΠΩΛΗΣΕΙΣ ΑΝΑ ΗΜΕΡΑ ΕΒΔΟΜΑΔΑΣ"
            periodLabel={periodLabel}
            loading={loading}
            data={data}
            columns={columns}
            totals={totals}
            fileName={`weekdays-${periodLabel.replace(/\s|→/g, "_")}`}
        >
            {peakLabel && (
                <div
                    className="inline-flex items-center gap-2 border-2 border-[#34C759] bg-[#34C759] text-white px-4 py-2"
                    data-testid="weekday-peak"
                >
                    <CalendarCheck size={14} weight="bold" />
                    <span className="text-[11px] font-bold uppercase tracking-[0.2em]">
                        ΚΟΡΥΦΑΙΑ ΗΜΕΡΑ:
                    </span>
                    <span className="font-mono font-black tracking-tight">
                        {peakLabel}
                    </span>
                </div>
            )}
            <div
                className="border border-[#E2E4E9] bg-white p-4"
                data-testid="weekdays-chart"
            >
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5F66] mb-2">
                    ΠΩΛΗΣΕΙΣ ΑΝΑ ΗΜΕΡΑ ΤΗΣ ΕΒΔΟΜΑΔΑΣ
                </div>
                <div style={{ width: "100%", height: 280, minHeight: 280 }}>
                    <ResponsiveContainer>
                        <BarChart
                            data={(data?.rows || []).map((r) => ({
                                day: r.label.slice(0, 3),
                                "Πωλήσεις": r.count,
                                "Τζίρος": r.gross,
                            }))}
                            margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E4E9" />
                            <XAxis
                                dataKey="day"
                                tick={{ fontSize: 11, fill: "#0A0A0A", fontWeight: 600 }}
                            />
                            <YAxis
                                yAxisId="left"
                                tick={{ fontSize: 11, fill: "#5C5F66" }}
                            />
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                tick={{ fontSize: 11, fill: "#5C5F66" }}
                                tickFormatter={(v) => `${v}€`}
                            />
                            <Tooltip
                                contentStyle={{
                                    border: "2px solid #0A0A0A",
                                    borderRadius: 0,
                                    fontFamily: "monospace",
                                    fontSize: 12,
                                }}
                            />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Bar yAxisId="left" dataKey="Πωλήσεις" fill="#34C759">
                                {(data?.rows || []).map((r, i) => (
                                    <Cell
                                        key={`wd-${i}`}
                                        fill={
                                            data?.peak_weekday === r.weekday
                                                ? "#002FA7"
                                                : "#34C759"
                                        }
                                    />
                                ))}
                            </Bar>
                            <Bar yAxisId="right" dataKey="Τζίρος" fill="#FFCC00" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
            <SimpleTable rows={data?.rows || []} columns={columns} totals={totals} />
        </ReportShell>
    );
}

// ===== COMPARE MONTHS =====
function CompareMonthsSection() {
    const monthOptions = useMemo(() => buildMonthOptions(), []);
    const [selected, setSelected] = useState(() =>
        monthOptions.slice(0, 3).map((o) => o.key)
    );
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        if (selected.length === 0) {
            setData(null);
            return;
        }
        setLoading(true);
        try {
            const r = await api.get("/reports/compare-months", {
                params: { months: selected.join(",") },
            });
            setData(r.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Αποτυχία φόρτωσης");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected.join(",")]);

    const toggleMonth = (key) => {
        setSelected((prev) => {
            if (prev.includes(key)) {
                return prev.filter((k) => k !== key);
            }
            if (prev.length >= 6) {
                toast.error("Μέγιστο 6 μήνες");
                return prev;
            }
            return [...prev, key];
        });
    };

    const chartData = (data?.months || [])
        .slice()
        .reverse() // chronological order
        .map((m) => ({
            month: monthLabel(m.month_key),
            "Σύνολο": m.total_gross,
            "Χρεώθηκε": m.amount_charged,
            "ΦΠΑ": m.total_vat,
        }));

    // For PDF/Excel
    const columns = [
        { key: "month_key", label: "ΜΗΝΑΣ", format: monthLabel },
        {
            key: "sales_count",
            label: "ΠΩΛΗΣΕΙΣ",
            align: "right",
            numeric: true,
            format: (v) => String(v),
        },
        {
            key: "unique_customers",
            label: "ΠΕΛΑΤΕΣ",
            align: "right",
            numeric: true,
            format: (v) => String(v),
        },
        {
            key: "unique_items",
            label: "ΕΙΔΗ",
            align: "right",
            numeric: true,
            format: (v) => String(v),
        },
        {
            key: "total_net",
            label: "ΚΑΘΑΡΑ",
            align: "right",
            numeric: true,
            format: fmtEUR,
        },
        {
            key: "total_vat",
            label: "ΦΠΑ",
            align: "right",
            numeric: true,
            format: fmtEUR,
        },
        {
            key: "total_gross",
            label: "ΣΥΝΟΛΟ",
            align: "right",
            numeric: true,
            format: fmtEUR,
        },
        {
            key: "amount_charged",
            label: "ΧΡΕΩΘΗΚΕ",
            align: "right",
            numeric: true,
            format: fmtEUR,
        },
        {
            key: "period_limit",
            label: "ΟΡΙΟ",
            align: "right",
            format: (v) => (v == null ? "—" : fmtEUR(v)),
        },
    ];

    // Aggregate totals across all selected months for the footer row.
    const totals = (data?.months && data.months.length > 0)
        ? [
              { key: "sales_count", value: data.months.reduce((a, m) => a + (m.sales_count || 0), 0), format: (v) => String(v) },
              { key: "unique_customers", value: data.months.reduce((a, m) => a + (m.unique_customers || 0), 0), format: (v) => String(v) },
              { key: "unique_items", value: data.months.reduce((a, m) => a + (m.unique_items || 0), 0), format: (v) => String(v) },
              { key: "total_net", value: data.months.reduce((a, m) => a + (m.total_net || 0), 0), format: fmtEUR },
              { key: "total_vat", value: data.months.reduce((a, m) => a + (m.total_vat || 0), 0), format: fmtEUR },
              { key: "total_gross", value: data.months.reduce((a, m) => a + (m.total_gross || 0), 0), format: fmtEUR },
              { key: "amount_charged", value: data.months.reduce((a, m) => a + (m.amount_charged || 0), 0), format: fmtEUR },
          ]
        : [];

    const exportPDF = async () => {
        if (!data?.months?.length) {
            toast.error("Καμία εγγραφή προς εξαγωγή");
            return;
        }
        try {
            await exportReportPDF({
                title: "ΣΥΓΚΡΙΣΗ ΜΗΝΩΝ",
                subtitle: data.months.map((m) => monthLabel(m.month_key)).join(", "),
                columns,
                rows: data.months,
                totals,
                fileName: `sigkrisi-minon-${selected.join("_")}`,
                orientation: "landscape",
            });
        } catch {
            toast.error("Σφάλμα εξαγωγής PDF");
        }
    };
    const exportXLS = () => {
        if (!data?.months?.length) {
            toast.error("Καμία εγγραφή προς εξαγωγή");
            return;
        }
        try {
            exportReportExcel({
                title: "ΣΥΓΚΡΙΣΗ ΜΗΝΩΝ",
                subtitle: data.months.map((m) => monthLabel(m.month_key)).join(", "),
                columns,
                rows: data.months,
                totals,
                fileName: `sigkrisi-minon-${selected.join("_")}`,
            });
        } catch {
            toast.error("Σφάλμα εξαγωγής Excel");
        }
    };

    return (
        <div className="space-y-4" data-testid="compare-section">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <h2 className="font-heading text-2xl font-black tracking-tighter">
                    ΣΥΓΚΡΙΣΗ ΜΗΝΩΝ
                </h2>
                <div className="flex items-center gap-2">
                    <Button
                        onClick={exportPDF}
                        data-testid="compare-export-pdf"
                        className="rounded-none h-10 bg-white border-2 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white font-bold text-xs uppercase tracking-wider"
                    >
                        <FilePdf size={14} weight="bold" className="mr-2" />
                        PDF
                    </Button>
                    <Button
                        onClick={exportXLS}
                        data-testid="compare-export-excel"
                        className="rounded-none h-10 bg-white border-2 border-[#1F8A3E] text-[#1F8A3E] hover:bg-[#1F8A3E] hover:text-white font-bold text-xs uppercase tracking-wider"
                    >
                        <FileXls size={14} weight="bold" className="mr-2" />
                        EXCEL
                    </Button>
                </div>
            </div>

            {/* Month picker chips */}
            <div className="border-2 border-[#0A0A0A] bg-white p-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5F66] mb-2">
                    ΕΠΙΛΟΓΗ ΜΗΝΩΝ (έως 6)
                </div>
                <div className="flex flex-wrap gap-2">
                    {monthOptions.map((o) => {
                        const active = selected.includes(o.key);
                        return (
                            <button
                                key={o.key}
                                onClick={() => toggleMonth(o.key)}
                                data-testid={`compare-chip-${o.key}`}
                                className={`px-3 h-9 text-xs font-bold uppercase tracking-wider border-2 transition-colors ${
                                    active
                                        ? "bg-[#002FA7] text-white border-[#002FA7]"
                                        : "bg-white text-[#0A0A0A] border-[#E2E4E9] hover:border-[#002FA7]"
                                }`}
                            >
                                {o.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {loading ? (
                <div className="p-12 text-center text-sm text-[#5C5F66]">
                    Φόρτωση...
                </div>
            ) : !data || data.months.length === 0 ? (
                <div className="p-12 text-center text-sm text-[#5C5F66] border border-[#E2E4E9] bg-white">
                    Επιλέξτε τουλάχιστον έναν μήνα.
                </div>
            ) : (
                <>
                    {/* Chart */}
                    <div className="border border-[#E2E4E9] bg-white p-4">
                        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5F66] mb-2">
                            ΣΥΓΚΡΙΣΗ ΣΥΝΟΛΩΝ ΑΝΑ ΜΗΝΑ
                        </div>
                        <div style={{ width: "100%", height: 320, minHeight: 320 }}>
                            <ResponsiveContainer>
                                <BarChart
                                    data={chartData}
                                    margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                                >
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        stroke="#E2E4E9"
                                    />
                                    <XAxis
                                        dataKey="month"
                                        tick={{ fontSize: 11, fill: "#5C5F66" }}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 11, fill: "#5C5F66" }}
                                        tickFormatter={(v) => `${v}€`}
                                    />
                                    <Tooltip
                                        formatter={(v) => fmtEUR(v)}
                                        contentStyle={{
                                            border: "2px solid #0A0A0A",
                                            borderRadius: 0,
                                            fontFamily: "monospace",
                                            fontSize: 12,
                                        }}
                                    />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    <Bar dataKey="Σύνολο" fill="#002FA7" />
                                    <Bar dataKey="Χρεώθηκε" fill="#34C759" />
                                    <Bar dataKey="ΦΠΑ" fill="#FFCC00" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Side-by-side metric cards */}
                    <div
                        className="grid gap-3"
                        style={{
                            gridTemplateColumns: `repeat(${data.months.length}, minmax(0, 1fr))`,
                        }}
                    >
                        {data.months.map((m) => (
                            <div
                                key={m.month_key}
                                className="border-2 border-[#0A0A0A] bg-white p-4"
                                data-testid={`compare-card-${m.month_key}`}
                            >
                                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5F66]">
                                    {monthLabel(m.month_key)}
                                </div>
                                <div className="font-heading text-3xl font-black tracking-tighter text-[#002FA7] mt-1">
                                    {fmtEUR(m.total_gross)}
                                </div>
                                <div className="text-xs text-[#5C5F66] mt-1 font-mono">
                                    Σύνολο αγορών
                                </div>
                                <div className="mt-3 space-y-1 text-xs">
                                    <Row k="Πωλήσεις" v={m.sales_count} />
                                    <Row k="Πελάτες" v={m.unique_customers} />
                                    <Row k="Είδη" v={m.unique_items} />
                                    <Row k="Καθαρά" v={fmtEUR(m.total_net)} />
                                    <Row k="ΦΠΑ" v={fmtEUR(m.total_vat)} />
                                    <Row
                                        k="Χρεώθηκε"
                                        v={fmtEUR(m.amount_charged)}
                                    />
                                    <Row
                                        k="Όριο μήνα"
                                        v={
                                            m.period_limit == null
                                                ? "—"
                                                : fmtEUR(m.period_limit)
                                        }
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Detailed comparison table */}
                    <SimpleTable
                        rows={data.months}
                        columns={columns}
                        totals={totals}
                    />
                </>
            )}
        </div>
    );
}

function Row({ k, v }) {
    return (
        <div className="flex justify-between border-b border-[#E2E4E9] py-1">
            <span className="text-[#5C5F66]">{k}</span>
            <span className="font-mono font-bold">{v}</span>
        </div>
    );
}

// ===== Reusable plain table =====
function SimpleTable({ rows, columns, totals }) {
    return (
        <div className="border border-[#E2E4E9] bg-white overflow-x-auto">
            <Table>
                <TableHeader>
                    <TableRow className="bg-[#0A0A0A] hover:bg-[#0A0A0A]">
                        {columns.map((c) => (
                            <TableHead
                                key={c.key}
                                className={`text-white text-[10px] uppercase tracking-wider ${
                                    c.align === "right" ? "text-right" : ""
                                }`}
                            >
                                {c.label}
                            </TableHead>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((r, i) => (
                        <TableRow
                            key={i}
                            className="border-b border-[#E2E4E9] hover:bg-[#F4F5F7]"
                        >
                            {columns.map((c) => (
                                <TableCell
                                    key={c.key}
                                    className={`text-sm ${
                                        c.align === "right"
                                            ? "text-right font-mono"
                                            : ""
                                    }`}
                                >
                                    {c.format
                                        ? c.format(r[c.key], r)
                                        : r[c.key] ?? "—"}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                    {totals && totals.length > 0 && (
                        <TableRow className="bg-[#F4F5F7] font-bold">
                            {columns.map((c, i) => {
                                const t = totals.find((t) => t.key === c.key);
                                return (
                                    <TableCell
                                        key={c.key}
                                        className={`font-bold ${
                                            c.align === "right"
                                                ? "text-right font-mono"
                                                : ""
                                        }`}
                                    >
                                        {i === 0
                                            ? "ΣΥΝΟΛΑ"
                                            : t
                                              ? t.format(t.value)
                                              : ""}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
    );
}



// ===== RETURNS =====
function fmtDateGR(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("el-GR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function ReturnsSection({ params, periodLabel }) {
    const { data, loading } = useReport("/reports/returns", params);
    const rows = data?.rows || [];
    const top = data?.top_customers || [];
    const totals = data?.totals || { count: 0, amount: 0 };

    const exportColumns = [
        {
            key: "created_at",
            label: "ΗΜ/ΝΙΑ ΕΠΙΣΤΡΟΦΗΣ",
            format: (v) => fmtDateGR(v),
        },
        { key: "customer_code", label: "ΚΩΔΙΚΟΣ" },
        { key: "customer_name", label: "ΠΕΛΑΤΗΣ" },
        {
            key: "items_count",
            label: "ΕΙΔΗ",
            align: "right",
            numeric: true,
            format: (v) => String(v),
        },
        { key: "items_summary", label: "ΑΝΑΛΥΣΗ" },
        {
            key: "original_sale_date",
            label: "ΑΡΧΙΚΗ ΠΩΛΗΣΗ",
            format: (v) => (v ? fmtDateGR(v) : "—"),
        },
        {
            key: "amount",
            label: "ΑΞΙΑ ΕΠΙΣΤΡΟΦΗΣ",
            align: "right",
            numeric: true,
            format: fmtEUR,
        },
    ];
    const exportTotals = [
        { key: "items_count", value: totals.count, format: (v) => String(v) },
        { key: "amount", value: totals.amount, format: fmtEUR },
    ];
    const fileBase = `epistrofes-${periodLabel.replace(/\s|→/g, "_")}`;

    const handleExportPDF = async () => {
        if (rows.length === 0) {
            toast.error("Καμία εγγραφή προς εξαγωγή");
            return;
        }
        try {
            await exportReportPDF({
                title: "ΕΠΙΣΤΡΟΦΕΣ",
                subtitle: periodLabel,
                columns: exportColumns,
                rows,
                totals: exportTotals,
                fileName: fileBase,
                orientation: "landscape",
            });
        } catch {
            toast.error("Σφάλμα εξαγωγής PDF");
        }
    };
    const handleExportXLS = () => {
        if (rows.length === 0) {
            toast.error("Καμία εγγραφή προς εξαγωγή");
            return;
        }
        try {
            exportReportExcel({
                title: "ΕΠΙΣΤΡΟΦΕΣ",
                subtitle: periodLabel,
                columns: exportColumns,
                rows,
                totals: exportTotals,
                fileName: fileBase,
            });
        } catch {
            toast.error("Σφάλμα εξαγωγής Excel");
        }
    };

    return (
        <div className="space-y-6" data-testid="returns-section">
            {/* Header */}
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5F66]">
                        {periodLabel}
                    </div>
                    <h2 className="font-heading text-3xl font-black tracking-tighter text-[#0A0A0A] flex items-center gap-2 mt-1">
                        <ArrowUUpLeft size={26} weight="bold" className="text-[#8B2530]" />
                        ΕΠΙΣΤΡΟΦΕΣ
                    </h2>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        onClick={handleExportPDF}
                        data-testid="returns-export-pdf"
                        className="rounded-none h-10 bg-white border-2 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white font-bold text-xs uppercase tracking-wider"
                    >
                        <FilePdf size={14} weight="bold" className="mr-2" />
                        PDF
                    </Button>
                    <Button
                        onClick={handleExportXLS}
                        data-testid="returns-export-excel"
                        className="rounded-none h-10 bg-white border-2 border-[#1F8A3E] text-[#1F8A3E] hover:bg-[#1F8A3E] hover:text-white font-bold text-xs uppercase tracking-wider"
                    >
                        <FileXls size={14} weight="bold" className="mr-2" />
                        EXCEL
                    </Button>
                </div>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border-2 border-[#8B2530] bg-[#FDEAE8] p-5">
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5F66]">
                        ΠΛΗΘΟΣ ΕΠΙΣΤΡΟΦΩΝ
                    </div>
                    <div
                        className="font-heading text-4xl font-black tracking-tighter text-[#8B2530] mt-1"
                        data-testid="returns-count"
                    >
                        {totals.count}
                    </div>
                </div>
                <div className="border-2 border-[#8B2530] bg-[#FDEAE8] p-5">
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5F66]">
                        ΣΥΝΟΛΟ ΑΞΙΑΣ ΕΠΙΣΤΡΟΦΩΝ
                    </div>
                    <div
                        className="font-heading text-4xl font-black tracking-tighter text-[#8B2530] mt-1"
                        data-testid="returns-total-amount"
                    >
                        {fmtEUR(totals.amount)}
                    </div>
                </div>
            </div>

            {/* Top customers */}
            {top.length > 0 && (
                <div className="border-2 border-[#0A0A0A] bg-white">
                    <div className="px-3 py-2 bg-[#0A0A0A] text-white text-[10px] font-black uppercase tracking-[0.2em] flex items-center gap-2">
                        <Trophy size={14} weight="bold" />
                        ΠΕΛΑΤΕΣ ΜΕ ΤΙΣ ΠΕΡΙΣΣΟΤΕΡΕΣ ΕΠΙΣΤΡΟΦΕΣ
                    </div>
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-[#F4F5F7] border-b-2 border-[#0A0A0A]">
                                <TableHead className="text-[10px] font-black uppercase tracking-wider">
                                    ΠΕΛΑΤΗΣ
                                </TableHead>
                                <TableHead className="text-[10px] font-black uppercase tracking-wider text-right">
                                    ΠΛΗΘΟΣ
                                </TableHead>
                                <TableHead className="text-[10px] font-black uppercase tracking-wider text-right">
                                    ΣΥΝΟΛΙΚΗ ΑΞΙΑ
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {top.map((c) => (
                                <TableRow
                                    key={c.customer_code}
                                    data-testid={`returns-top-${c.customer_code}`}
                                >
                                    <TableCell className="font-semibold">
                                        <span className="font-mono text-xs mr-2 text-[#5C5F66]">
                                            #{c.customer_code}
                                        </span>
                                        {c.customer_name}
                                    </TableCell>
                                    <TableCell className="text-right font-mono">
                                        {c.count}
                                    </TableCell>
                                    <TableCell className="text-right font-mono font-black text-[#8B2530]">
                                        {fmtEUR(c.amount)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Detailed list */}
            <div className="border-2 border-[#0A0A0A] bg-white">
                <div className="px-3 py-2 bg-[#0A0A0A] text-white text-[10px] font-black uppercase tracking-[0.2em] flex items-center gap-2">
                    <Receipt size={14} weight="bold" />
                    ΑΝΑΛΥΤΙΚΗ ΛΙΣΤΑ ΕΠΙΣΤΡΟΦΩΝ
                </div>
                <Table>
                    <TableHeader>
                        <TableRow className="bg-[#F4F5F7] border-b-2 border-[#0A0A0A]">
                            <TableHead className="text-[10px] font-black uppercase tracking-wider">
                                ΗΜ/ΝΙΑ ΕΠΙΣΤΡΟΦΗΣ
                            </TableHead>
                            <TableHead className="text-[10px] font-black uppercase tracking-wider">
                                ΠΕΛΑΤΗΣ
                            </TableHead>
                            <TableHead className="text-[10px] font-black uppercase tracking-wider">
                                ΕΙΔΗ
                            </TableHead>
                            <TableHead className="text-[10px] font-black uppercase tracking-wider">
                                ΑΡΧΙΚΗ ΠΩΛΗΣΗ
                            </TableHead>
                            <TableHead className="text-[10px] font-black uppercase tracking-wider text-right">
                                ΑΞΙΑ
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center text-sm text-[#5C5F66] py-6">
                                    Φόρτωση...
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center text-sm text-[#5C5F66] py-6">
                                    Δεν υπάρχουν επιστροφές για αυτό το διάστημα.
                                </TableCell>
                            </TableRow>
                        )}
                        {rows.map((r) => (
                            <TableRow
                                key={r.id}
                                data-testid={`returns-row-${r.id}`}
                                className="bg-[#FDEAE8]"
                            >
                                <TableCell className="font-mono text-xs">
                                    {fmtDateGR(r.created_at)}
                                </TableCell>
                                <TableCell className="font-semibold">
                                    <span className="font-mono text-xs mr-2 text-[#5C5F66]">
                                        #{r.customer_code}
                                    </span>
                                    {r.customer_name}
                                </TableCell>
                                <TableCell className="text-xs">
                                    <span className="text-[#5C5F66]">
                                        {r.items_count} είδη ·{" "}
                                    </span>
                                    {r.items_summary}
                                </TableCell>
                                <TableCell className="font-mono text-xs text-[#5C5F66]">
                                    {r.original_sale_date
                                        ? fmtDateGR(r.original_sale_date)
                                        : "—"}
                                </TableCell>
                                <TableCell className="text-right font-mono font-black text-[#8B2530]">
                                    −{fmtEUR(r.amount)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
